import React, { useContext, useEffect } from 'react';
import { Button } from '@material-ui/core';
import ReplyIcon from '@material-ui/icons/Reply';
import {
  CostCenterRecord,
  UserTableFilters,
  CostCenterUsageHistoryDetails,
  UsageRecord,
  UsageItem,
  UsersHistoryDetails,
} from '../../interfaces/types';
import {
  getSourceClassName,
  filterCostCenterData,
  formatCurrencyAbbreviated,
  getThemeCSSVariables,
} from '../../utils/utils';
import { UsageHistorySection } from '../usage-history-section/UsageHistorySection';
import { UsersSection } from '../users-section/UsersSection';
import {
  GitHubCostCenterApi,
  gitHubCostCenterRef,
} from '../../../../apis/gitHubCostCenterConfigApi';
import { FrontendConfigContext } from '../../../../providers/FrontendConfigProvider';
import { AuthContext } from '../../../../providers/AuthProvider';
import { useApi } from '@backstage/core-plugin-api';
import { Theme, makeStyles, createStyles } from '@material-ui/core';
import { useGitHubCostCenterEditBudgetEnabled } from '../../../../hooks/useFeatureFlag';

interface CostCenterDetailProps {
  selectedCostCenter: CostCenterRecord;
  selectedCostCenterData: CostCenterRecord[];
  displayPeriod: string;
  usageHistoryPage: number;
  usersPage: number;
  showUsersFilters: boolean;
  userTableFilters: UserTableFilters;
  activeUserFilterCount: number;
  usersFiltersDropdownRef: React.RefObject<HTMLDivElement | null>;
  executeRefresh: boolean;
  showLoadingBudget: boolean;
  onBack: () => void;
  onEditBudget: () => void;
  onUsageHistoryPageChange: (newPage: number) => void;
  onUsersPageChange: (newPage: number) => void;
  onToggleShowUsersFilters: () => void;
  onToggleUserTableFilter: (filterKey: keyof UserTableFilters) => void;
  onClearUserFilters: () => void;
  onExportUsageHistoryCSV: () => void;
  onExportUsersCSV: () => void;
  onRefreshCostCenterData: () => void;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    gitHubCostCenterThemeScope: getThemeCSSVariables(theme),
  }),
);

export const CostCenterDetail: React.FC<CostCenterDetailProps> = ({
  selectedCostCenter,
  selectedCostCenterData,
  displayPeriod,
  usageHistoryPage,
  usersPage,
  showUsersFilters,
  userTableFilters,
  activeUserFilterCount,
  usersFiltersDropdownRef,
  executeRefresh,
  showLoadingBudget,
  onBack,
  onEditBudget,
  onUsageHistoryPageChange,
  onUsersPageChange,
  onToggleShowUsersFilters,
  onToggleUserTableFilter,
  onClearUserFilters,
  onExportUsageHistoryCSV,
  onExportUsersCSV,
  onRefreshCostCenterData,
}) => {
  const classes = useStyles();
  const { profEmail, profileName, isAdmin } = useContext(AuthContext);
  const gitHubCostCenterApi: GitHubCostCenterApi = useApi(gitHubCostCenterRef);
  const { frontendConfigData } = useContext(FrontendConfigContext);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isBudgetUpdating, setIsBudgetUpdating] = React.useState(false);
  const [usageHistoryData, setUsageHistoryData] =
    React.useState<CostCenterUsageHistoryDetails>(
      {} as CostCenterUsageHistoryDetails,
    );
  const [historyLength, setHistoryLength] = React.useState<number>(0);
  const currentYear = new Date().getFullYear();
  const [usersData, setUsersData] = React.useState<UsersHistoryDetails>(
    {} as UsersHistoryDetails,
  );
  const editBudgetSplitDetails = useGitHubCostCenterEditBudgetEnabled();

  useEffect(() => {
    if (showLoadingBudget) setIsBudgetUpdating(true);
    if (showLoadingBudget === false) setIsBudgetUpdating(false);
  }, [showLoadingBudget]);

  useEffect(() => {
    if (
      frontendConfigData &&
      gitHubCostCenterApi &&
      (!usersData || Object.keys(usersData).length === 0)
    ) {
      getFilteredDetails();
    } else if (
      frontendConfigData &&
      gitHubCostCenterApi &&
      usersData &&
      Object.keys(usersData).length > 0 &&
      executeRefresh
    ) {
      getFilteredDetails();
      onRefreshCostCenterData?.();
    }
  }, [frontendConfigData, gitHubCostCenterApi, executeRefresh]);

  useEffect(() => {
    if (!!usageHistoryData && Object.keys(usageHistoryData).length > 0) {
      setHistoryLength(
        Object.keys(
          usageHistoryData?.monthAndYearWiseCostCenters?.[currentYear] || {},
        ).length,
      );
    }
  }, [usageHistoryData]);

  const getFilteredDetails = async () => {
    try {
      setIsLoading(true);
      const allResults = await Promise.allSettled([
        gitHubCostCenterApi.getCostCenters(selectedCostCenter.source),
        gitHubCostCenterApi.getUsersList(
          selectedCostCenter.source,
          'any',
          '' + selectedCostCenter.year,
          '' + selectedCostCenter.month,
        ),
      ]);
      let totalGrossAmount = 0;
      let totalNetAmount = 0;
      const noUsageDetailsUsers: any[] = [];

      if (selectedCostCenter.resources?.length) {
        const filteredUsers: UsageRecord[] = [];
        selectedCostCenter.resources.forEach(resource => {
          const userEmail = resource.email;
          const userData =
            allResults[1].status === 'fulfilled'
              ? allResults[1].value?.data.find(
                  (user: UsageRecord) =>
                    user.email === userEmail && resource.name === user.user,
                )
              : undefined;
          if (userData) {
            userData.totalGrossAmount = 0;
            userData.totalNetAmount = 0;
            if (userData?.usageItems?.length) {
              userData.usageItems.forEach((item: UsageItem) => {
                userData.totalGrossAmount += item.grossAmount || 0;
                userData.totalNetAmount += item.netAmount || 0;
              });
            }
            totalGrossAmount += userData.totalGrossAmount;
            totalNetAmount += userData.totalNetAmount;
            filteredUsers.push(userData);
          } else {
            noUsageDetailsUsers.push(resource);
            filteredUsers.push({
              email: resource.email,
              month: selectedCostCenter.month,
              source: selectedCostCenter.source,
              usageItems: [],
              user: resource.name,
              year: selectedCostCenter.year,
              totalGrossAmount: 0,
              totalNetAmount: 0,
            } as UsageRecord);
          }
        });

        setUsersData({
          additionalInfo: {
            totalGrossAmount,
            totalNetAmount,
            noUsageDetailsUsers,
          },
          usersData: filteredUsers,
        });
      } else {
        setUsersData({} as UsersHistoryDetails);
      }
      const filteredData = filterCostCenterData(
        allResults[0].status === 'fulfilled'
          ? allResults[0].value?.data || []
          : [],
        selectedCostCenter.name,
        selectedCostCenter.source,
        true,
        true,
      ) as { monthAndYear: any; filteredCostCenters: CostCenterRecord[] };
      setUsageHistoryData({
        originalCostCenter:
          allResults[0].status === 'fulfilled'
            ? allResults[0].value?.data || []
            : [],
        filteredCostCenter: filteredData.filteredCostCenters,
        monthAndYearWiseCostCenters: filteredData.monthAndYear,
      });
    } catch (error) {
      console.log('error', error);
    } finally {
      setIsLoading(false);
    }
  };

  const isAllowedUserForEditBuget = () => {
    const billingApprover = // profEmail || // local testing
      selectedCostCenter?.subscriptionData?.tags?.BillingApprover;
    return (
      billingApprover &&
      billingApprover.toLowerCase() === profEmail?.toLowerCase()
    );
  };

  return (
    <div
      className={`${classes.gitHubCostCenterThemeScope} cost-center-detail-page`}
    >
      {/* Back Button */}
      <Button onClick={onBack} color="primary">
        <ReplyIcon /> Back
      </Button>

      <div className="cost-center-detail-hero-container">
        <span>
          <i>
            Note: Monthly Co-Pilot license fees are excluded from totals shown
            on this dashboard. Please note that the monthly Co-Pilot license
            fees are not included in the budget that is set for your cost center
            and can cause your monthly Azure bill to exceed the allocated
            budget.
          </i>
        </span>
        {/* Header Section */}
        <div className="cost-center-detail-hero">
          <div className="cost-center-detail-title-row">
            {/* Line 1: Name + Chips */}
            <div className="cost-center-detail-header-line">
              <h1>{selectedCostCenter.name}</h1>
              <div className="cost-center-card__badges cost-center-detail-hero__badges">
                <span
                  className={`cost-center-pill cost-center-pill--source ${getSourceClassName(
                    selectedCostCenter.source,
                  )}`}
                >
                  {selectedCostCenter.source}
                </span>
                <span className="cost-center-pill cost-center-pill--status">
                  {selectedCostCenter.state}
                </span>
              </div>
            </div>

            {/* Line 2: Azure subscription */}
            <p className="cost-center-detail-meta">
              <span>Azure subscription:</span>{' '}
              {selectedCostCenter.azureSubscription}
            </p>

            {/* Line 3: Allocated budget + Edit button */}
            <div className="cost-center-detail-budget-row">
              <p className="cost-center-detail-budget">
                <span className="cost-center-detail-budget-label">
                  Allocated budget:&nbsp;
                </span>
                <span className="cost-center-detail-budget-value">
                  {formatCurrencyAbbreviated(
                    selectedCostCenter.budgetAmount || 0,
                  )}
                </span>
              </p>
              {editBudgetSplitDetails && (
                <Button
                  variant="contained"
                  color="primary"
                  role="button"
                  data-testid="edit-budget-btn"
                  size="small"
                  onClick={onEditBudget}
                  disabled={
                    isLoading ||
                    isBudgetUpdating ||
                    (isAllowedUserForEditBuget() ? false : true)
                  }
                >
                  Edit Budget
                </Button>
              )}

              {isBudgetUpdating && (
                <div className="pulsing-loader-effect">
                  <div></div>
                  <div></div>
                  <div></div>
                </div>
              )}
            </div>

            <div className="cost-center-detail-budget-row billing-approver-container">
              <p className="cost-center-detail-budget">
                <span className="cost-center-detail-budget-label">
                  Billing Approver:&nbsp;
                </span>
                <b className="cost-center-detail-budget-value">
                  {selectedCostCenter?.subscriptionData?.tags?.BillingApprover}
                </b>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Usage History Section */}
      <UsageHistorySection
        selectedCostCenterDataLength={historyLength}
        usageHistoryPage={usageHistoryPage}
        onPageChange={onUsageHistoryPageChange}
        onExportCSV={onExportUsageHistoryCSV}
        monthAndYearWiseCostCenters={
          usageHistoryData?.monthAndYearWiseCostCenters
        }
        isLoading={isLoading}
      />

      {/* Users Section */}
      <UsersSection
        selectedCostCenter={selectedCostCenter}
        displayPeriod={displayPeriod}
        showUsersFilters={showUsersFilters}
        userTableFilters={userTableFilters}
        activeUserFilterCount={activeUserFilterCount}
        usersPage={usersPage}
        usersFiltersDropdownRef={usersFiltersDropdownRef}
        onToggleShowUsersFilters={onToggleShowUsersFilters}
        onToggleUserTableFilter={onToggleUserTableFilter}
        onClearUserFilters={onClearUserFilters}
        onExportCSV={onExportUsersCSV}
        onPageChange={onUsersPageChange}
        isLoading={isLoading}
        usersData={usersData}
      />
    </div>
  );
};
